import { SmallBadge } from '@talentmesh/ui/components/badge';
import { Button } from '@talentmesh/ui/components/button';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { cn } from '@talentmesh/ui/lib/utils';
import { Calendar, DollarSign, MapPin, Users, XIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCompanyLookup } from '../../api/useCompanyLookup';
import CompanyLogo from '../CompanyLogo/CompanyLogo';
import CompanyDetailPointList from './CompanyDetailPointList';
import ResourceNotFoundError from '@/Utils/HTTP/Errors/ResourceNotFoundError';
import UIStrings from '@/Utils/UIStrings';

// Below this, a wait is indistinguishable from a normal cache-hit lookup; above it,
// we're almost certainly in the slow "deep research" path (avg. ~10s).
const SLOW_LOOKUP_THRESHOLD_MS = 2000;
const SLOW_LOOKUP_TICK_MS = 250;

export interface CompanyDetailProps {
    companyDomain: string | null;
    /** used as fallback */
    companyName: string;
    companyLinkedInUrl: string | null;
    onCloseIconClick?: () => void;
}

const CompanyDetail = ({
    companyDomain,
    companyName: companyNameFallback,
    companyLinkedInUrl,
    onCloseIconClick,
}: CompanyDetailProps) => {
    const { data, isLoading, error } = useCompanyLookup(companyNameFallback, companyDomain, companyLinkedInUrl);
    const [overflowWidth, setOverflowWidth] = useState(0);
    const [elapsedLoadingMs, setElapsedLoadingMs] = useState(0);

    useEffect(() => {
        if (!isLoading) {
            setElapsedLoadingMs(0);
            return;
        }

        const startedAt = Date.now();
        const interval = setInterval(() => setElapsedLoadingMs(Date.now() - startedAt), SLOW_LOOKUP_TICK_MS);

        return () => clearInterval(interval);
    }, [isLoading]);

    const isSlowLookup = isLoading && elapsedLoadingMs > SLOW_LOOKUP_THRESHOLD_MS;
    const componentWidth = useMemo(() => {
        const baseWidth = 386;

        return `${baseWidth + overflowWidth}px`;
    }, [overflowWidth]);
    const companyName = data?.name || companyNameFallback;
    const targetRef = useCallback(
        (target: HTMLSpanElement) => {
            if (!target || !data?.industry) {
                return;
            }

            const observer = new ResizeObserver((entries) => {
                entries.forEach((entry) => {
                    const overflownWidth = entry.target.scrollWidth - entry.target.clientWidth;
                    if (overflownWidth > 0) {
                        setOverflowWidth(overflownWidth);
                    }
                });
            });

            observer.observe(target);
        },
        [setOverflowWidth, data?.industry],
    );

    return (
        <div
            data-cy="companyDetailContainer"
            className="flex w-full flex-col gap-4 p-5"
            style={{ width: componentWidth }}
        >
            <div className="flex w-full gap-4">
                <div className="self-center">
                    <CompanyLogo companyDomain={companyDomain} companyName={companyName} size="40px" />
                </div>
                <div className="flex w-full flex-col justify-center gap-1 overflow-hidden">
                    {isLoading ? (
                        <h3 className="text-xl font-medium">{companyName}</h3>
                    ) : (
                        <a
                            href={data?.website!}
                            target="_blank"
                            rel="noreferrer"
                            className={cn('text-foreground w-fit', data?.website ? 'hover:underline' : '')}
                        >
                            <h3 className="text-xl font-medium">{companyName}</h3>
                        </a>
                    )}
                    {!isLoading && data?.type && <p className="text-muted-foreground text-sm">{data.type}</p>}
                    {!isLoading && data?.industry && (
                        <span ref={targetRef} className="w-auto overflow-hidden whitespace-nowrap">
                            <SmallBadge variant="default" className="w-max">
                                {data.industry}
                            </SmallBadge>
                        </span>
                    )}
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="close button"
                    className="ml-auto"
                    onClick={onCloseIconClick}
                >
                    <XIcon />
                </Button>
            </div>
            {isLoading ? (
                <div className="flex flex-col items-center gap-2 py-4" role="status" aria-live="polite">
                    <Spinner className="text-primary size-6" />
                    {isSlowLookup && (
                        <p className="text-muted-foreground mt-2 text-center text-sm">
                            {UIStrings.CompanyResearchInProgress}
                        </p>
                    )}
                </div>
            ) : error instanceof ResourceNotFoundError ? (
                <p className="text-xs">{UIStrings.InformationNotAvailable}</p>
            ) : (
                <>
                    {data?.description && <p className="text-xs leading-relaxed">{data.description}</p>}
                    <div className="grid grid-cols-2 gap-x-2.5 gap-y-3">
                        <CompanyDetailPointList
                            icon={<DollarSign className="size-4 shrink-0" />}
                            label={UIStrings.Revenue}
                            value={data?.revenueTextFormatted}
                        />
                        <CompanyDetailPointList
                            icon={<Users className="size-4 shrink-0" />}
                            label={UIStrings.Employees}
                            value={data?.employeesCount}
                        />
                        <CompanyDetailPointList
                            icon={<Calendar className="size-4 shrink-0" />}
                            label={UIStrings.Founded}
                            value={data?.foundedAt}
                        />
                        <CompanyDetailPointList
                            icon={<MapPin className="size-4 shrink-0" />}
                            label={UIStrings.HQ}
                            value={data?.headquarter}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default CompanyDetail;
